import { useCallback, useEffect, useRef, useState } from "react"

import exifr from "exifr"

import { useDateFormatter } from "@l2r-front/l2r-i18n"

import { useImageCollectionStateContext, useImageCollectionDispatchContext } from "../../contexts/ImageCollectionContext"
import { useNearestImageInCollection } from "../../hooks/queries/imageCollection/useNearestImageInCollection"
import { usePhoto } from "../../hooks/queries/imageCollection/usePhoto"

import { PhotoSphereViewer } from "../PhotoSphereViewer/PhotoSphereViewer"

import { PanoramicViewerError } from "./PanoramicViewer.error"
import * as Styled from "./PanoramicViewer.styled"

const DEFAULT_YAW = Math.PI

export function PanoramicViewer(props) {

    const { active = true, initialPosition, date } = props

    const dateFormatter = useDateFormatter()
    const { imageCollection, selectedImageCollectionRoad, displayedImagePosition, imageAngle } = useImageCollectionStateContext()
    const { setDisplayedImagePosition, setImageAngle } = useImageCollectionDispatchContext()
    const [targetPosition, setTargetPosition] = useState(null)
    const pendingAngleRef = useRef(null)
    const wasActiveRef = useRef(false)
   
    if (active && !wasActiveRef.current) {
        pendingAngleRef.current = typeof imageAngle === "number" ? imageAngle : null
    }
    wasActiveRef.current = active

    const bearingRef = useRef(0)
    const offsetRef = useRef(0)

    const {
        imageFeature,
        isLoading: isLoadingImageCollections,
        isError: isErrorImageCollection,
    } = useNearestImageInCollection(imageCollection, selectedImageCollectionRoad, initialPosition)

    const {
        data: panoData,
        isLoading: isLoadingPhoto,
        isError: isErrorPhoto,
    } = usePhoto(imageFeature?.id)

    const handlePositionChange = useCallback((_pitch, yaw) => {
        offsetRef.current = (DEFAULT_YAW - yaw) * (180 / Math.PI)
        if (active) {
            setImageAngle(convertAngle(bearingRef.current + offsetRef.current))
        }
    }, [active, setImageAngle])

    useEffect(() => {
        if (active && panoData) {
            exifr.parse(panoData, true)
                .then((exifData) => {
                    if (exifData?.GPSDestBearing) {
                        bearingRef.current = exifData.GPSDestBearing
                        setImageAngle(convertAngle(bearingRef.current + offsetRef.current))
                    }
                    if (pendingAngleRef.current !== null) {
                        setTargetPosition({ yaw: angleToYaw(pendingAngleRef.current, bearingRef.current) })
                        pendingAngleRef.current = null
                    }
                })
                .catch((error) => console.error("failed to read panorama exif data", error))
        }
        return () => {
            if (active) {
                setImageAngle(null)
            }
        }
    }, [active, panoData, setImageAngle])

    const [imageUrl, setImageUrl] = useState()
    const hasViewerRef = useRef(false)

    if (imageUrl) {
        hasViewerRef.current = true
    }

    useEffect(() => {
        if (imageFeature?.id && panoData && !isErrorPhoto) {
            const blob = new Blob([panoData], { type: "application/octet-stream" })
            const url = URL.createObjectURL(blob)
            setImageUrl(url)
            return () => {
                URL.revokeObjectURL(url)
            }
        } else {
            setImageUrl(undefined)
        }
    }, [panoData, imageFeature?.id, isErrorPhoto])

    useEffect(() => {
        if (!active) {
            return
        }
        if (imageFeature
            && displayedImagePosition?.lat !== imageFeature.geometry.coordinates[1]
            && displayedImagePosition?.lng !== imageFeature.geometry.coordinates[0]) {
            setDisplayedImagePosition({ lat: imageFeature.geometry.coordinates[1], lng: imageFeature.geometry.coordinates[0] })
        } else if (!imageFeature && !isLoadingImageCollections) {
            setDisplayedImagePosition(null)
        }
    }, [active, imageFeature, displayedImagePosition, setDisplayedImagePosition, isLoadingImageCollections])

    const isLoading = isLoadingImageCollections || (isLoadingPhoto && !!imageFeature)
    const isError = isErrorImageCollection || isErrorPhoto
    const showError = isError || (!isLoading && !imageFeature)

    return (
        <>
            <Styled.Container id="panoramic-viewer">
                {hasViewerRef.current && (
                    <Styled.ViewerWrapper hidden={showError}>
                        <PhotoSphereViewer imageUrl={imageUrl} onPositionChange={handlePositionChange} targetPosition={targetPosition} />
                    </Styled.ViewerWrapper>
                )}
                <Styled.DateContainer>
                    {date && <Styled.DateLabel>{dateFormatter(new Date(date), "MMMM y")}</Styled.DateLabel>}
                </Styled.DateContainer>
            </Styled.Container>
            {isLoading && <Styled.LoadingContainer><Styled.Skeleton /></Styled.LoadingContainer>}
            {showError && <PanoramicViewerError />}
        </>
    )
}

function convertAngle(angle) {
    return Math.round(((-90 - angle) % 360 + 360) % 360)
}

function angleToYaw(angle, bearing) {
    const yawDegrees = angle + bearing + 270
    return (((yawDegrees % 360) + 360) % 360) * (Math.PI / 180)
}
